<template>
	<div ref="bs" class="b-scroll-container">
		<div class="b-scroll-wrapper">
			<div class="scroll-self">
				<!-- TODO 样式 -->
				<div
					v-if="pullDown"
					class="pull-down-wrapper"
					:class="{
						'pull-down--visible': isPullingInProgress,
						'pull-down--transition': isPullingInTransition
					}"
				>
					<span>{{ isPullingInProgress ? refreshHint : pullDownHint }}</span>
				</div>

				<!-- content -->
				<div ref="listWrapper" :class="contentClassName">
					<slot></slot>
				</div>

				<div v-if="pullUp && moreData" class="pull-up-wrapper">{{ $t('加载中') }}...</div>
			</div>
		</div>
	</div>
</template>

<script>
import { getRect, debounce } from '@/utils/api';
import BScroll from 'better-scroll';

export default {
	props: {
		/**
		 * 开启下拉加载
		 */
		pullDown: {
			type: Boolean,
			default: false
		},
		/**
		 * 开启上拉加载
		 */
		pullUp: {
			type: Boolean,
			default: false
		},
		/**
		 * 是否还有更多 配合上拉加载控制
		 */
		moreData: {
			type: Boolean,
			default: false
		},
		/** 外接插槽类名, 配合 /deep/ 使用 */
		contentClassName: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			bScroll: null,
			pullDownHint: this.$t('下拉刷新'),
			refreshHint: '',
			isPullingInProgress: false,
			isPullingInTransition: false
		};
	},
	mounted() {
		this.init();
		window.addEventListener('resize', this.listenResize);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.listenResize);
	},
	methods: {
		getScrollY() {
			return this.bScroll && this.bScroll.y;
		},
		scrollTo(...args) {
			this.bScroll && this.bScroll.scrollTo(...args);
		},
		scrollToElement(...args) {
			this.bScroll && this.bScroll.scrollToElement(...args);
		},
		refresh() {
			this.bScroll && this.bScroll.refresh();
		},
		async init() {
			// 确保DOM已经渲染完成再初始化
			await this.$nextTick();

			if (this.$refs.listWrapper) {
				this.$refs.listWrapper.style.minHeight = `${getRect(this.$refs.bs).height + 1}px`;
			}

			const options = {
				mouseWheel: true, // Better-Scroll 将接管滚轮事件，从而阻止了原生的滚动行为（滚动条）
				click: /OpenHarmony|openHarmony|Android|webOS|iPhone|iPod/i.test(navigator.userAgent), // 允许点击事件穿透
				scrollX: false,
				scrollY: true, // 滚动方向为垂直
				probeType: 3 // listening scroll event
			};
			if (this.pullDown) {
				options.pullDownRefresh = {
					stop: 0
				};
			}
			if (this.pullUp) {
				options.pullUpLoad = true;
			}
			this.bScroll = new BScroll(this.$refs.bs, options);

			if (this.pullDown) {
				this.bScroll.on('scroll', pos => {
					// 在一些页面跳转的场景（预览文件），返回页面后会发现该字段为 false 从而导致无法滚动
					if (!this.bScroll.hasVerticalScroll) {
						this.refresh();
					}

					const { y } = pos;
					this.$emit('scroll', pos);

					if (y > 50) {
						this.pullDownHint = this.$t('释放加载');
					} else {
						this.pullDownHint = this.$t('下拉刷新');
					}
				});
				this.bScroll.on('touchEnd', pos => {
					if (this.isPullingInProgress) return;
					const { y } = pos;
					if (y > 50) {
						this.isPullingInProgress = true;
						this.refreshHint = `${this.$t('加载中')}...`;
						this.startPullDownRefresh();
					}
				});
			}
			if (this.pullUp) {
				this.bScroll.on('pullingUp', () => {
					if (!this.moreData) {
						// 通过调用 finishPullUp 确保完成一次完整的上拉加载流程
						// 避免下拉重置后导致上拉监听失效的问题
						this.bScroll.finishPullUp();
						return;
					}
					this.startPullUpRefresh();
				});
			}
		},
		async startPullDownRefresh() {
			console.log('PullDown start.');

			// 使其至少有特定时间（600）的延迟
			const ensureMinimumDelay = () => {
				return new Promise(resolve =>
					setTimeout(() => {
						resolve(true);
					}, 600)
				);
			};
			const handlePullDown = () => {
				return new Promise(resolve => {
					const done = () => {
						resolve(true);
					};
					this.$emit('pull-down', done);
				});
			};
			await Promise.all([ensureMinimumDelay, handlePullDown].map(fn => fn()));

			console.log('PullDown complete.');

			// Stop 停顿一会以展示成功状态
			this.refreshHint = this.$t('刷新完成');
			await new Promise(resolve =>
				setTimeout(() => {
					resolve(true);
				}, 500)
			);

			const transitionTime = 220;
			this.isPullingInTransition = true;
			setTimeout(() => {
				this.isPullingInTransition = false;
				this.isPullingInProgress = false;
				this.bScroll.finishPullDown();
				this.bScroll.refresh();
				console.log('PullDown done.');
			}, transitionTime);
		},

		async startPullUpRefresh() {
			console.log('PullUp start.');

			await new Promise(resolve => {
				const done = () => {
					resolve(true);
				};
				this.$emit('pull-up', done);
			});

			console.log('PullUp done.');

			this.bScroll.finishPullUp();
			this.bScroll.refresh();
		},
		listenResize: debounce(function () {
			this.bScroll && this.bScroll.destroy();
			this.init();
		})
	}
};
</script>

<style lang="less" scoped>
.b-scroll-container {
	height: 100%;
	overflow: hidden;

	.b-scroll-wrapper {
		position: relative;

		.pull-down-wrapper {
			position: absolute;
			top: -44px;
			left: 0;
			right: 0;
			text-align: center;
			line-height: 44px;
			font-size: 14px;
			height: 44px;
			opacity: 1;

			&.pull-down--visible {
				position: static;
			}
			&.pull-down--transition {
				height: 0;
				opacity: 0;
				transition: all 0.22s ease;
				/* Firefox 4 */
				-moz-transition: all 0.22s ease;
				/* Safari 和 Chrome */
				-webkit-transition: all 0.22s ease;
				/* Opera */
				-o-transition: all 0.22s ease;
			}
		}
		.pull-up-wrapper {
			text-align: center;
			line-height: 44px;
			font-size: 14px;
			height: 44px;
		}
	}
}
</style>
